import { useEffect, useState, useRef } from "react";
import { api, HEADS, HEAD_COLORS } from "@/lib/api";
import { useWeek } from "@/context/WeekContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import { AlertCircle, TrendingUp, TrendingDown, Search, Zap, Trophy, Activity } from "lucide-react";
import DrilldownModal from "@/components/DrilldownModal";
import { Skeleton } from "@/components/ui/skeleton";

const Sparkline = ({ data }) => {
  if (!data || data.length === 0) return <div className="w-28 h-8 bg-slate-50 rounded" />;
  return (
    <div className="w-28 h-8 bg-slate-50/50 rounded border border-slate-100 px-1">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <Line type="monotone" dataKey="total" stroke="#1E40AF" strokeWidth={1.75} dot={false} isAnimationActive={false} />
          <RTooltip
            wrapperStyle={{ zIndex: 100 }}
            contentStyle={{ fontSize: "10px", padding: "4px 6px" }}
            labelFormatter={(v) => `W: ${v}`}
            formatter={(v) => [v, "Total"]}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const DeltaCell = ({ delta }) => {
  if (delta === 0 || delta === undefined) return <span className="font-mono text-xs text-slate-400">0</span>;
  const up = delta > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono text-xs font-semibold ${up ? "text-red-600" : "text-emerald-600"}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? "+" : ""}{delta}
    </span>
  );
};

const KpiCard = ({ label, value, sub, tone = "default", icon: Icon, testid }) => {
  const tones = {
    default: "border-slate-200",
    danger: "border-red-200 bg-red-50/40",
    success: "border-emerald-200 bg-emerald-50/40",
    warning: "border-amber-200 bg-amber-50/40",
  };
  return (
    <Card data-testid={testid} className={`p-4 border ${tones[tone]}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{label}</span>
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}
      </div>
      <div className="font-display font-bold text-2xl text-slate-900">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{sub}</div>}
    </Card>
  );
};

export default function Dashboard() {
  const { week, refresh: refreshWeeks } = useWeek();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState({});
  const [search, setSearch] = useState("");
  const [filterHead, setFilterHead] = useState("all");
  const [filterAlert, setFilterAlert] = useState("all");
  const [compare, setCompare] = useState("wow");
  const [drilldownPair, setDrilldownPair] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqRef = useRef(0);

  const load = async () => {
    const reqId = ++reqRef.current;
    setLoading(true);
    try {
      const [aggR, sumR] = await Promise.all([
        api.get("/pair-aggregates", { params: { week_label: week, compare } }),
        api.get("/summary", { params: { week_label: week, compare } }),
      ]);
      if (reqRef.current !== reqId) return;
      setData(aggR.data);
      setSummary(sumR.data);
      setLoading(false);
      // trends for visible pairs (top 40 by total)
      const rows = (aggR.data.rows || []).slice(0, 40);
      const trendMap = {};
      await Promise.all(
        rows.map(async (r) => {
          const { data } = await api.get(`/trends/${encodeURIComponent(r.pair_code)}`, { params: { weeks: 8 } });
          trendMap[r.pair_code] = data.series;
        })
      );
      if (reqRef.current !== reqId) return;
      setTrends(trendMap);
    } catch {
      if (reqRef.current === reqId) setLoading(false);
    }
  };

  useEffect(() => { if (week) load(); }, [week, compare]);

  const seedAndLoad = async () => {
    await api.post("/seed-sample");
    await refreshWeeks();
    await load();
  };

  if (!week) {
    return (
      <div className="text-center py-20">
        <Activity className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h2 className="font-display text-xl font-semibold text-slate-800 mb-2">No data yet</h2>
        <p className="text-sm text-slate-500 mb-6">Upload a weekly RailMADAD file to get started, or load sample data.</p>
        <div className="flex gap-3 justify-center">
          <Button data-testid="seed-sample-btn" onClick={seedAndLoad} className="bg-blue-600 hover:bg-blue-500 text-white">
            Load Sample Data
          </Button>
          <Button data-testid="goto-upload-btn" variant="outline" onClick={() => window.location.assign("/upload")}>
            Upload File
          </Button>
        </div>
      </div>
    );
  }

  const rows = (data?.rows || []).filter((r) => {
    if (search && !`${r.pair_code} ${r.pair_name}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterAlert === "alerts" && !r.any_alert) return false;
    if (filterAlert === "worsening" && r.total_delta <= 0) return false;
    if (filterHead !== "all" && (r.counts[filterHead] || 0) === 0) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <KpiCard
          testid="kpi-total"
          label="Total Complaints"
          value={summary?.total_complaints ?? "—"}
          sub={`Week ${data?.week_label || ""}`}
          icon={Activity}
        />
        <KpiCard
          testid="kpi-wow"
          label={compare === "yoy" ? "YoY Δ" : "WoW Δ"}
          value={summary ? `${summary.wow_delta > 0 ? "+" : ""}${summary.wow_delta}` : "—"}
          sub={summary?.prev_week ? `vs ${summary.prev_week} (${summary.wow_delta_pct.toFixed(1)}%)` : (compare === "yoy" ? "no same week last year" : "no prev week")}
          tone={summary?.wow_delta > 0 ? "danger" : "success"}
          icon={summary?.wow_delta > 0 ? TrendingUp : TrendingDown}
        />
        <KpiCard
          testid="kpi-alerts"
          label="Flagged Alerts"
          value={summary?.alert_count ?? "—"}
          sub={`≥ threshold on any head`}
          tone="danger"
          icon={AlertCircle}
        />
        <KpiCard
          testid="kpi-unmatched"
          label="Unmatched Trains"
          value={summary?.unmatched_count ?? "—"}
          sub="Not in 135-pair master"
          tone="warning"
          icon={Zap}
        />
        <KpiCard
          testid="kpi-top-head"
          label="Top Head"
          value={summary?.top_head ? summary.top_head.split(" ")[0] : "—"}
          sub={summary?.top_head ? `${summary.head_totals[summary.top_head]} complaints` : ""}
          icon={Trophy}
        />
      </div>

      {/* Filters */}
      <Card className="p-4 border border-slate-200">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              data-testid="train-pair-search-input"
              placeholder="Search by pair code or name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
          <Select value={filterHead} onValueChange={setFilterHead}>
            <SelectTrigger data-testid="head-filter-select" className="h-9 w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Heads</SelectItem>
              {HEADS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterAlert} onValueChange={setFilterAlert}>
            <SelectTrigger data-testid="alert-filter-toggle" className="h-9 w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Pairs</SelectItem>
              <SelectItem value="alerts">Flagged Alerts Only</SelectItem>
              <SelectItem value="worsening">Worsening Only</SelectItem>
            </SelectContent>
          </Select>
          <Select value={compare} onValueChange={setCompare}>
            <SelectTrigger data-testid="compare-mode-select" className="h-9 w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="wow">vs Previous Week</SelectItem>
              <SelectItem value="yoy">vs Same Week Last Year</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-xs text-slate-500 font-mono ml-auto">
            {rows.length} / {data?.rows?.length || 0} pairs
          </div>
        </div>
      </Card>

      {/* Main table */}
      <Card className="border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[1200px] text-sm">
            <thead>
              <tr className="bg-slate-900 text-white text-[11px] uppercase tracking-wider">
                <th className="sticky left-0 z-20 bg-slate-900 text-left px-3 py-3 sticky-col-shadow">Pair</th>
                <th className="text-left px-3 py-3">Name</th>
                {HEADS.map((h) => (
                  <th key={h} className="text-center px-2 py-3" style={{ color: HEAD_COLORS[h].bg }}>
                    {h.split(" ")[0]}<br/><span className="text-[9px] opacity-60">{h.split(" ")[1] || ""}</span>
                  </th>
                ))}
                <th className="text-right px-3 py-3">Total</th>
                <th className="text-center px-3 py-3">8-Wk Trend</th>
                <th className="text-right px-3 py-3">{compare === "yoy" ? "Δ YoY" : "Δ WoW"}</th>
                <th className="text-center px-3 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}><td colSpan={10} className="p-2"><Skeleton className="h-8 w-full" /></td></tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={10} className="text-center py-12 text-slate-400 text-sm">No pairs match filters</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr
                  key={r.pair_code}
                  data-testid={`pair-row-${r.pair_code}`}
                  onClick={() => setDrilldownPair(r)}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer group"
                >
                  <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-3 py-2.5 font-mono text-xs font-semibold text-slate-900 sticky-col-shadow">
                    {r.pair_code}
                  </td>
                  <td className="px-3 py-2.5 text-slate-700 text-xs">{r.pair_name}</td>
                  {HEADS.map((h) => {
                    const c = r.counts[h] || 0;
                    const d = r.deltas[h] || 0;
                    const alert = r.alerts[h];
                    return (
                      <td key={h} className="text-center px-2 py-2.5">
                        <div className={`inline-flex flex-col items-center gap-0.5 px-2 py-1 rounded ${alert ? "bg-red-50 ring-1 ring-red-200" : ""}`}>
                          <span className="font-mono text-sm font-semibold" style={{ color: alert ? "#DC2626" : HEAD_COLORS[h].text }}>{c}</span>
                          <DeltaCell delta={d} />
                        </div>
                      </td>
                    );
                  })}
                  <td className="text-right px-3 py-2.5 font-mono font-bold text-slate-900">{r.total}</td>
                  <td className="px-3 py-2.5"><Sparkline data={trends[r.pair_code]} /></td>
                  <td className="text-right px-3 py-2.5"><DeltaCell delta={r.total_delta} /></td>
                  <td className="text-center px-3 py-2.5">
                    {r.any_alert ? (
                      <Badge data-testid={`alert-badge-${r.pair_code}`} className="bg-red-100 text-red-700 border border-red-200 alert-pulse text-[10px] font-bold uppercase">
                        Alert
                      </Badge>
                    ) : (
                      <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] uppercase">OK</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {drilldownPair && (
        <DrilldownModal pair={drilldownPair} week={data.week_label} onClose={() => setDrilldownPair(null)} />
      )}
    </div>
  );
}
